import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { io } from "socket.io-client";
import config from '../../config';
import statusDictionary from '../../helpers/statusDictionary.json';
import { handlers } from './../../helpers/helpers';
import { patientsService } from '../../services/patient';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';

const FINISHED_STATES = [statusDictionary.done.status, statusDictionary.inProgress.status];


const useStepHandler = ({ data, countryCode, getExamsOfDay }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState();
  const [loading, setLoading] = useState();
  const [userData, setUserData] = useState();
  const [steps, setSteps] = useState();
  const [reviewCode, setReviewCode] = useState();
  const [unfinishHealthConsultation, setUnfinishHealthConsultation] = useState();
  const [unfinishPsychologicalConsultation, setUnfinishPsychologicalConsultation] = useState();
  const [hasHealthConsultations, setHasHealthConsultations] = useState();
  const [hasPsychologicalConsultations, setHasPsychologicalConsultations] = useState();
  const [finishSteps, setFinishSteps] = useState();
  const [disableContinueButton, setDisableContinueButton] = useState(false);
  const [status, setStatus] = useState();
  const [socket, setSocket] = useState();
  const { t } = useTranslation();

  const initSocket = () => {
    const socket = io(config.socketServer, {
      transports: ['websocket']
    });
    setSocket(socket);
  }

  useEffect(() => {
    setCurrentStep();
    setLoading();
    setUserData();
    setSteps();
    setStatus();
    initSocket();
  }, []);

  useEffect(() => {
    return () => {
      if (socket) {
        socket.disconnect();
      }
    }
  }, [socket]);


  const generateReviewCode = useCallback(() => {
    if (userData) {
      patientsService.generateReviewCode({
        document_number: userData.document_number,
        document_type: userData.document_type,
      }).then((response) => {
        setReviewCode(response);
      }).catch((error) => {
        message.error(error.message);
      })
    }
  }, [userData]);

  useEffect(() => {
    setSteps([
      {
        title: t('patientAttentionStatePage.stepHandler.step1'),
        description: t('patientAttentionStatePage.stepHandler.step1Description'),
        current: false,
        done: false,
      },
      {
        title: t('patientAttentionStatePage.stepHandler.step2'),
        description: t('patientAttentionStatePage.stepHandler.step2Description'),
        current: false,
        done: false,
      },
    ]);
    setStatus(statusDictionary.wait.status);
    setLoading(false);
    setUserData(data);
  }, [data, t]);

  const updateExamState = useCallback((params) => {
    if (params.admission_patient_exam_id && params.state && userData) {
      setUserData({
        ...userData, exams: userData.exams.map((exam) => {
          if (exam.admission_patient_exam_id === params.admission_patient_exam_id) {
            exam.state = params.state;
            setStatus(params.done ? statusDictionary.done.status : statusDictionary.wait.status);

            if (currentStep !== 0) {
              setCurrentStep(0);
            } else {
              setCurrentStep((prevState) => prevState);
            }
          }

          return exam;
        })
      });
    }
  }, [userData, currentStep]);

  const updateConsultations = useCallback((params, consultations) => {
    return consultations.map((consultation) => {
      if (consultation.id === params.consultation_id) {
        consultation.state = params.done ? statusDictionary.done.status : statusDictionary.inProgress.status;
        setStatus(params.done ? statusDictionary.done.status : statusDictionary.wait.status);

        if (currentStep !== 1) {
          setCurrentStep(1);
        } else {
          setCurrentStep((prevState) => prevState);
        }
      }

      return consultation;
    })
  }, [currentStep])

  const updateConsultationState = useCallback((params) => {
    if (params.consultation_id && params.done !== undefined && userData) {
      setUserData({
        ...userData, consultations: updateConsultations(params, userData.consultations)
      });
    }
  }, [userData, updateConsultations]);

  const updateConsultationStateByExam = useCallback((params) => {
    if (params.consultation_id && params.done !== undefined) {
      getExamsOfDay();
    }
  }, [getExamsOfDay]);

  useEffect(() => {
    if (socket) {
      const examListeners = [];
      const consultationListeners = [];

      userData?.exams?.forEach((exam) => {
        if (exam?.admission_patient_exam_id) {
          const eventName = `admission_patient_exam_update_${exam.admission_patient_exam_id}`;
          const handler = (data) => {
            updateExamState(data);
          };
          socket.on(eventName, handler);
          examListeners.push({ eventName, handler });
        }
      });

      const batchHandler = (data) => {
        data.admission_patient_exam_ids.forEach((id) => {
          updateExamState({ admission_patient_exam_id: id, state: data.state });
        });
      };
      socket.on('admission_patient_exam_batch_update', batchHandler);

      userData?.consultations?.forEach((consultation) => {
        if (consultation?.id) {
          const eventName = `consultation_update_${consultation.id}`;
          const handler = (data) => {
            updateConsultationState(data);
          };
          socket.on(eventName, handler);
          consultationListeners.push({ eventName, handler });
        }
      });

      return () => {
        examListeners.forEach(({ eventName, handler }) => {
          socket.off(eventName, handler);
        });
        socket.off('admission_patient_exam_batch_update', batchHandler);
        consultationListeners.forEach(({ eventName, handler }) => {
          socket.off(eventName, handler);
        });
      };
    }
  }, [socket, updateConsultationState, updateConsultationStateByExam, updateExamState, userData]);

  useEffect(() => {
    if (socket && userData?.admission_patient_id) {
      const eventName = `consultation_created_${userData.admission_patient_id}`;
      const handler = (data) => {
        console.log('consultation_created', data);
        updateConsultationStateByExam(data);
      };
      socket.on(eventName, handler);
      return () => {
        socket.off(eventName, handler);
      };
    }
  }, [socket, updateConsultationStateByExam, userData?.admission_patient_id]);

  const setStatusByStep = useCallback((step) => {
    if (step === 0) {
      const unfinishedExams = userData.exams.filter((exam) => {
        return !FINISHED_STATES.includes(exam.state);
      });

      setDisableContinueButton(unfinishedExams.length > 0);

      if (unfinishedExams.length > 0) {
        setStatus(statusDictionary.wait.status);
      } else {
        setStatus(statusDictionary.done.status);
      }
    } else if (step === 1) {
      const unfinishedConsulations = userData.consultations?.filter((consultation) => {
        return consultation.state !== statusDictionary.done.status;
      })

      const unfinishedHealthConsultation = unfinishedConsulations?.filter((consultation) => {
        return consultation.exam_type === 'health' && consultation.state !== statusDictionary.done.status;
      });

      const unfinishedPsychologicalConsultation = unfinishedConsulations?.filter((consultation) => {
        return consultation.exam_type === 'psychological' && consultation.state !== statusDictionary.done.status;
      });

      setHasHealthConsultations(userData.consultations?.filter((consultation) => { return consultation.exam_type === 'health' }).length > 0);
      setHasPsychologicalConsultations(userData.consultations?.filter((consultation) => { return consultation.exam_type === 'psychological' }).length > 0);

      setDisableContinueButton(unfinishedConsulations === undefined || unfinishedConsulations?.length > 0);
      setUnfinishHealthConsultation(unfinishedHealthConsultation);
      setUnfinishPsychologicalConsultation(unfinishedPsychologicalConsultation);

      if (!userData.consultations || unfinishedConsulations?.length) {
        setStatus(statusDictionary.wait.status);
      } else {
        setStatus(statusDictionary.done.status);
        generateReviewCode();
      }
    }
  }, [generateReviewCode, userData])

  useEffect(() => {
    if (userData) {
      if (userData.current_step !== undefined) {
        setCurrentStep(userData.current_step);
        if (userData.current_step === 0) {
          setStatusByStep(0);
        } else if (userData.current_step === 1) {
          setStatusByStep(1);
        }
      } else {
        setCurrentStep(0);
        setUserData({ ...userData, current_step: 0 });
        handlers.handleUserData({ ...userData, current_step: 0 });
      }
    }
  }, [setStatusByStep, userData]);

  const getStepStatus = useCallback((step) => {
    let status = statusDictionary.wait.status;
    if (userData) {
      if (step === 0) {
        const unfinishedExams = userData.exams?.filter((exam) => {
          return !FINISHED_STATES.includes(exam.state);
        })

        if (unfinishedExams.length === 0) {
          status = statusDictionary.done;
        }
      } else {
        const unfinishedConsulations = userData.consultations?.filter((consultation) => {
          return consultation.state !== statusDictionary.done.status;
        })

        if (unfinishedConsulations?.length === 0) {
          status = statusDictionary.done.status;
        }
      }
    }
    return status;
  }, [userData]);

  useEffect(() => {
    if (currentStep !== undefined && status) {
      setSteps(steps.map((step, index) => {
        const isDone = getStepStatus(index) === statusDictionary.done.status;
        if (index === currentStep) {
          step.current = true;
          step.done = isDone;
        } else {
          step.current = false;
          step.done = isDone;
        }
        return step;
      }));
    }
  }, [currentStep]);

  useEffect(() => {
    if (userData) {
      const unfinishedExams = userData.exams?.filter((exam) => {
        return !FINISHED_STATES.includes(exam.state);
      });

      const unfinishedConsulations = userData.consultations?.filter((consultation) => {
        return consultation.state !== statusDictionary.done.status;
      });

      if (currentStep === 0 && unfinishedExams.length) {
        setDisableContinueButton(true);
      }

      if (currentStep === 1 && unfinishedConsulations?.length) {
        setDisableContinueButton(true);
      }
    }
  }, [userData, currentStep])

  const continueButtonHandler = useCallback(() => {
    if (currentStep + 1 < steps.length) {
      setUserData({ ...userData, current_step: currentStep + 1 })
      handlers.handleUserData({ ...userData, current_step: currentStep + 1 });
      setCurrentStep(currentStep + 1);
      setStatus(statusDictionary.wait.status);
    } else if (currentStep === 1 && status === statusDictionary.done.status && !finishSteps) {
      setFinishSteps(true);
    } else {
      localStorage.clear();
      navigate(`/mis-examenes/${countryCode}`)
    }
  }, [currentStep, steps, userData, status, countryCode, navigate, finishSteps]);

  return {
    currentStep,
    loading,
    continueButtonHandler,
    userData,
    steps,
    status,
    disableContinueButton,
    unfinishHealthConsultation,
    unfinishPsychologicalConsultation,
    finishSteps,
    reviewCode,
    hasHealthConsultations,
    hasPsychologicalConsultations,
  };
}

export default useStepHandler;
